<!--
  ContactElement — one record_contacts row as a line: the value in link blue
  ("ada@acme.io", a profile's handle, a place).

  The line leads with the contact's icon (its kind's, or a brand's for a known
  profile URL) where kinds mix or the field is selected; a field of one kind
  leaves it to its column header. A tall grid row adds a detail line: the
  contact's label and whether it's primary. Read-only, the value links to
  mail, call, the page or the map; `fixed` (a dropdown suggestion) never
  navigates. Not read-only, a × removes it.

  Props:
    - value: a record_contacts row ({ contact_type, value, label?, is_primary? }).
    - showKind: lead with the icon (default on, for surfaces outside the grid).
    - detail: show the detail line.
-->
<template>
  <span class="contact-element" :title="contact.value">
    <span class="contact-line">
      <font-awesome-icon v-if="showKind" :icon="icon" class="contact-kind" fixed-width />
      <component
        :is="interactive ? 'a' : 'span'"
        v-bind="interactive ? { href, target: '_blank', rel: 'noopener noreferrer' } : {}"
        class="contact-value"
      >
        {{ text }}
      </component>
      <button
        v-if="!readonly"
        type="button"
        class="contact-remove"
        aria-label="Remove contact"
        @click.stop.prevent="emit('remove')"
      >
        <font-awesome-icon icon="xmark" size="sm" />
      </button>
    </span>
    <span v-if="detail && detailText" class="contact-detail">{{ detailText }}</span>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { contactHref, contactIcon, type ContactRow, contactText } from "@auger/ui/utils/contacts"

const props = withDefaults(
  defineProps<{
    value: ContactRow
    showKind?: boolean
    detail?: boolean
    size?: string
    readonly?: boolean
    /** Render as a plain token, never a link — for dropdown suggestions,
     *  which select on click. */
    fixed?: boolean
  }>(),
  { showKind: true, detail: false, size: "normal", readonly: true, fixed: false }
)

const emit = defineEmits<{ remove: [] }>()

// The prop is named `value` to match the element-component convention; the
// payload is a contact row with its own `.value`.
const contact = computed(() => props.value)

const icon = computed(() => contactIcon(contact.value))
const text = computed(() => contactText(contact.value))
const href = computed(() => contactHref(contact.value))
const interactive = computed(() => props.readonly && !props.fixed && !!href.value)

/** "work · primary": a location's label is already its text. */
const detailText = computed(() => {
  const label = contact.value.contact_type === "location" ? null : contact.value.label
  return [label, contact.value.is_primary ? "primary" : null].filter(Boolean).join(" · ")
})
</script>

<style>
.contact-element {
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;

  .contact-line {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    min-width: 0;
    white-space: nowrap;
  }

  .contact-kind {
    flex: none;
    color: var(--auger-text-weak);
    font-size: 0.9em;
  }

  .contact-value {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--auger-link);
    text-decoration: none;
  }

  a.contact-value:hover {
    color: var(--auger-link-dark);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .contact-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: none;
    color: var(--auger-text-weak);
    cursor: pointer;

    &:hover {
      background: var(--auger-bg-muted);
      color: var(--auger-text);
    }
  }

  .contact-detail {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.9em;
    color: var(--auger-text-weak);
  }

  /* Under the text, not under the icon. */
  &:has(.contact-kind) .contact-detail {
    padding-left: calc(1.25em + 0.4em);
  }
}
</style>
